import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUser } from '../store/slice/userSlice';

function EditUserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedUser, loading, error, fetchUserById, updateUser } = useUser();
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (id) fetchUserById(id);
  }, [id, fetchUserById]);

  useEffect(() => {
    if (selectedUser) {
      setFormData({
        full_name: selectedUser.full_name || '',
        email: selectedUser.email || '',
        password: '',
        role: selectedUser.role || 'Customer',
        status: selectedUser.status || 'Active',
        phone: selectedUser.phone || '',
      });
    }
  }, [selectedUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData?.full_name?.trim() || !formData?.email?.trim()) return;

    const payload = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      role: formData.role,
      status: formData.status,
      phone: formData.phone.trim() || undefined,
    };
    if (formData.password) {
      payload.password = formData.password;
    }

    const result = await updateUser({ id, userData: payload });
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success('User updated successfully');
      navigate('/users');
    }
  };

  if ((loading && !selectedUser) || !formData) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div className="space-y-4">
        <Link to="/users" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium">
          ← Back to Users
        </Link>
        <div className="card-premium py-16 text-center text-slate-500">User not found.</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <Link
          to="/users"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Users
        </Link>
      </div>

      <div className="card-premium overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h1 className="font-display font-bold text-2xl text-slate-800 tracking-tight">Edit User</h1>
          <p className="mt-1 text-slate-500">Update user details</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-slate-700 mb-1.5">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="e.g., Sarah Johnson"
              className="input-premium"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., sarah@example.com"
              className="input-premium"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
              New Password <span className="text-slate-400 font-normal">(leave blank to keep current)</span>
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="input-premium"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1.5">
              Phone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="e.g., +1 234 567 8900"
              className="input-premium"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-1.5">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="input-premium"
              >
                <option value="Admin">Admin</option>
                <option value="Stylist">Stylist</option>
                <option value="Customer">Customer</option>
              </select>
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-slate-700 mb-1.5">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-premium"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <Link
              to="/users"
              className="px-6 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-premium hover:bg-slate-50 transition-all"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditUserPage;
